/**
 * Reading and writing the file every shop already has.
 *
 * A tailoring shop's customer list arrives as whatever the person who kept it could export:
 * a comma-separated file from a phone contacts app, a semicolon-separated one from an Indian
 * Excel install whose locale uses the comma as a decimal point, or a tab-separated paste out
 * of Google Sheets. All three are the same file to a shop owner, so all three are the same
 * file here — the delimiter is DETECTED rather than demanded, because "your file must be a
 * comma-separated CSV" is the sentence that ends an import before it starts.
 *
 * The parser is written out rather than taken from a package on purpose: this codebase writes
 * its own PDF and its own SigV4, and a dependency that runs over a customer's whole address
 * book is not one to add for two hundred lines of well-specified behaviour (RFC 4180, plus
 * the two real-world deviations below).
 *
 * Nothing here knows what a customer is. It answers "what is in this file?" and stops; what
 * the columns MEAN is `customer-import.ts`, and whether a row may be written is the API's.
 */

/** Delimiters worth guessing between. Order is the tie-break: comma wins a draw. */
const CANDIDATE_DELIMITERS = [',', ';', '\t', '|'] as const;
export type Delimiter = (typeof CANDIDATE_DELIMITERS)[number];

/** A parsed file: the header row as written, and every row under it. */
export interface DelimitedTable {
  /** The first row, trimmed. Empty strings are kept — a blank heading is still a column. */
  readonly header: readonly string[];
  /**
   * Every subsequent row, padded to the header's width so a short line does not shift its
   * own cells one column to the left of everybody else's.
   */
  readonly rows: readonly DelimitedRow[];
  readonly delimiter: Delimiter;
}

export interface DelimitedRow {
  /**
   * The line number in the file the person will open in Excel — 1-based and counting the
   * header, so "row 14 failed" names the row they can actually go and look at.
   */
  readonly rowNumber: number;
  readonly cells: readonly string[];
}

/**
 * The delimiter this text is most likely using.
 *
 * Counted OUTSIDE quoted sections, on the first few lines: a file of addresses is full of
 * commas inside quotes, and counting those would pick the comma for a tab-separated file
 * every time. The winner is the candidate whose count per line is both non-zero and the most
 * consistent — a real delimiter appears the same number of times on every row, while a comma
 * inside prose does not.
 */
export function detectDelimiter(text: string): Delimiter {
  const sample = text
    .split(/\r?\n/)
    .slice(0, 20)
    .filter((line) => line.trim().length > 0);
  if (sample.length === 0) return ',';

  let best: Delimiter = ',';
  let bestScore = -1;
  for (const candidate of CANDIDATE_DELIMITERS) {
    const counts = sample.map((line) => countOutsideQuotes(line, candidate));
    const total = counts.reduce((sum, count) => sum + count, 0);
    if (total === 0) continue;
    const first = counts[0] ?? 0;
    const consistent = counts.every((count) => count === first) ? 1 : 0;
    // Consistency dominates volume: three tabs on every line beats forty commas on one.
    const score = consistent * 1000 + total;
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

function countOutsideQuotes(line: string, delimiter: string): number {
  let count = 0;
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      quoted = !quoted;
      continue;
    }
    if (!quoted && character === delimiter) count += 1;
  }
  return count;
}

/**
 * Split one delimited document into rows of cells.
 *
 * RFC 4180 with the two deviations every real file has: a UTF-8 byte-order mark (Excel writes
 * one, and a heading whose first character is that invisible mark matches nothing), and a
 * lone `\r` or `\n` as a line
 * ending. A quote inside a quoted field is doubled, and a newline inside one is part of the
 * value — an address field routinely contains both.
 */
export function parseDelimitedRows(text: string, delimiter: string): string[][] {
  const input = text.charCodeAt(0) === 0xfe_ff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let index = 0;

  const endField = (): void => {
    row.push(field);
    field = '';
  };
  const endRow = (): void => {
    endField();
    rows.push(row);
    row = [];
  };

  while (index < input.length) {
    const character = input[index] as string;

    if (quoted) {
      if (character === '"') {
        // A doubled quote is a literal one; a single quote closes the field.
        if (input[index + 1] === '"') {
          field += '"';
          index += 2;
          continue;
        }
        quoted = false;
        index += 1;
        continue;
      }
      field += character;
      index += 1;
      continue;
    }

    if (character === '"' && field.length === 0) {
      quoted = true;
      index += 1;
      continue;
    }
    if (character === delimiter) {
      endField();
      index += 1;
      continue;
    }
    if (character === '\r' || character === '\n') {
      endRow();
      // Consume the second half of a CRLF so it does not read as an empty row.
      index += character === '\r' && input[index + 1] === '\n' ? 2 : 1;
      continue;
    }

    field += character;
    index += 1;
  }

  // A file that does not end in a newline still has a last row.
  if (field.length > 0 || row.length > 0) endRow();

  return rows;
}

/**
 * A delimited file as a header and a body.
 *
 * Wholly empty rows are dropped — a spreadsheet exported with trailing blank lines is the
 * normal case, and reporting "row 214 has no name" for a row the owner cannot see in Excel
 * makes the import look broken.
 */
export function parseDelimitedTable(text: string, delimiter?: string): DelimitedTable {
  const chosen = (delimiter ?? detectDelimiter(text)) as Delimiter;
  const raw = parseDelimitedRows(text, chosen);

  const headerIndex = raw.findIndex((row) => row.some((cell) => cell.trim().length > 0));
  if (headerIndex < 0) return { header: [], rows: [], delimiter: chosen };

  const header = (raw[headerIndex] ?? []).map((cell) => cell.trim());
  const rows: DelimitedRow[] = [];
  for (let index = headerIndex + 1; index < raw.length; index += 1) {
    const cells = raw[index] ?? [];
    if (cells.every((cell) => cell.trim().length === 0)) continue;
    rows.push({ rowNumber: index + 1, cells: padTo(cells, header.length) });
  }

  return { header, rows, delimiter: chosen };
}

function padTo(cells: readonly string[], width: number): string[] {
  const out = cells.map((cell) => cell.trim());
  while (out.length < width) out.push('');
  return out;
}

/**
 * Write rows back out as CSV.
 *
 * This is what the rejected-rows file is made of, so it has one job beyond correctness: the
 * file must open in the same spreadsheet it came from, be corrected in place, and be
 * re-uploaded. That means quoting anything containing a delimiter, a quote or a newline, and
 * quoting a leading `+` or `=` too — Excel would otherwise read `+919876543210` as a formula
 * and show the owner an error where their customer's phone number should be.
 */
export function toCsv(rows: readonly (readonly string[])[]): string {
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
}

function escapeCsvCell(value: string): string {
  const needsQuotes = /[",\r\n]/.test(value) || /^[=+\-@]/.test(value);
  return needsQuotes ? `"${value.replace(/"/g, '""')}"` : value;
}
